import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import {
  CollectionBloodIcon,
  CollectionBuccalIcon,
  CollectionSalivaIcon,
  StyledSalivaOnlyAtHomeText,
  StyledSamplesInfoByType,
  StyledSamplesInfoByTypeContent,
  StyledSamplesInfoByTypeIcon,
  StyledSampleTypeContainer,
  StyledSampleTypesList,
  StyledSampleTypeText,
  StyledValuePercentageInfoContainer,
} from './SampleInfoByType.styles';
import { SampleSerieType, SampleCollectionLocation } from '@modules/study-insights/types';
import { SampleInfoByTypeProps } from './SampleInfoByType.types';

import SampleComposition from '../SampleComposition/SampleComposition';
import { InfoTitle } from '@builder';

const SampleInfoByType: FC<SampleInfoByTypeProps> = ({
  collectionLocation,
  data,
  description,
  id,
  isSeriesEmpty,
  title,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();
  const isBuccalEnabled = isFeatureEnabled(FeatureFlags.BUCCAL_SAMPLE_TYPE);
  const [selectedType, setSelectedType] = useState<SampleSerieType>(SampleSerieType.SALIVA);

  // Reset to saliva if buccal is selected but feature flag gets disabled
  useEffect(() => {
    if (selectedType === SampleSerieType.BUCCAL_SWAB && !isBuccalEnabled) {
      setSelectedType(SampleSerieType.SALIVA);
    }
  }, [isBuccalEnabled, selectedType]);

  const handleSampleTypeSelection = (type: SampleSerieType): void => {
    setSelectedType(type);
  };

  const isSampleTypeSelected = (type: SampleSerieType): boolean => {
    return selectedType === type;
  };

  const getSelectedTypeLabel = (): string => {
    const labelMap: Record<SampleSerieType, string> = {
      [SampleSerieType.SALIVA]: t('sequenced_samples.saliva'),
      [SampleSerieType.BLOOD]: t('sequenced_samples.blood'),
      [SampleSerieType.BUCCAL_SWAB]: t('sequenced_samples.buccal'),
    };
    return labelMap[selectedType] || selectedType;
  };

  return (
    <StyledSamplesInfoByType>
      <InfoTitle testId="samples-info-by-type-title">{`${title} ${
        selectedType && ` (${getSelectedTypeLabel()})`
      }`}</InfoTitle>
      <StyledSamplesInfoByTypeContent>
        <StyledSampleTypesList>
          <StyledSampleTypeContainer>
            <StyledSamplesInfoByTypeIcon
              data-testid="samples-info-by-type-icon"
              onClick={() => handleSampleTypeSelection(SampleSerieType.SALIVA)}
              selected={isSampleTypeSelected(SampleSerieType.SALIVA)}
            >
              <CollectionSalivaIcon />
            </StyledSamplesInfoByTypeIcon>
            <StyledSampleTypeText selected={isSampleTypeSelected(SampleSerieType.SALIVA)}>
              {t('sequenced_samples.saliva')}
            </StyledSampleTypeText>
          </StyledSampleTypeContainer>
          {isBuccalEnabled && (
            <StyledSampleTypeContainer>
              <StyledSamplesInfoByTypeIcon
                data-testid="samples-info-by-type-icon"
                onClick={() => handleSampleTypeSelection(SampleSerieType.BUCCAL_SWAB)}
                selected={isSampleTypeSelected(SampleSerieType.BUCCAL_SWAB)}
              >
                <CollectionBuccalIcon />
              </StyledSamplesInfoByTypeIcon>
              <StyledSampleTypeText selected={isSampleTypeSelected(SampleSerieType.BUCCAL_SWAB)}>
                {t('sequenced_samples.buccal')}
              </StyledSampleTypeText>
            </StyledSampleTypeContainer>
          )}
          {collectionLocation !== SampleCollectionLocation.AT_HOME && (
            <StyledSampleTypeContainer>
              <StyledSamplesInfoByTypeIcon
                data-testid="samples-info-by-type-icon"
                onClick={() => handleSampleTypeSelection(SampleSerieType.BLOOD)}
                selected={isSampleTypeSelected(SampleSerieType.BLOOD)}
              >
                <CollectionBloodIcon />
              </StyledSamplesInfoByTypeIcon>
              <StyledSampleTypeText selected={isSampleTypeSelected(SampleSerieType.BLOOD)}>
                {t('sequenced_samples.blood')}
              </StyledSampleTypeText>
            </StyledSampleTypeContainer>
          )}
          {description && <StyledSalivaOnlyAtHomeText>{description}</StyledSalivaOnlyAtHomeText>}
        </StyledSampleTypesList>
        <StyledValuePercentageInfoContainer>
          <SampleComposition
            css={{ flex: '0 0 200px' }}
            details={t('sequenced_samples.of_sequenced_samples')}
            id={`${id}-sequenced-samples-sequenced`}
            isSeriesEmpty={isSeriesEmpty}
            percentage={data?.[selectedType]?.VALID?.percentage.toFixed(0) ?? 0}
            sampleName={t('sequenced_samples.sequenced')}
            total={data?.[selectedType]?.VALID?.total ?? 0}
          />
          <SampleComposition
            css={{ flex: '0 0 200px' }}
            details={t('sequenced_samples.of_recollect_samples')}
            id={`${id}-sequenced-samples-recollect`}
            isSeriesEmpty={isSeriesEmpty}
            percentage={data?.[selectedType]?.RECOLLECT?.percentage.toFixed(0) ?? 0}
            sampleName={t('sequenced_samples.recollects')}
            total={data?.[selectedType]?.RECOLLECT?.total ?? 0}
          />
        </StyledValuePercentageInfoContainer>
      </StyledSamplesInfoByTypeContent>
    </StyledSamplesInfoByType>
  );
};

export default SampleInfoByType;
